import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {toastActions} from './toast'
import {getRandId, readURIFromFile} from '../libs'
import {getCroppedImage} from '../libs/crop_image'
import {RoomDesignService} from '../api'
import {currentUserActions} from './currentUser'
import {selectBeforeImageData, selectServiceSettings} from '../selectors'
import {MaxPhotoUpload, MaxUploadFileSize, MaxRegenCount} from '../constants'
import { applyWatermark, WatermarkOptions, dataURLtoBlob, blobToDataURL, fetchImageAsBlob, convertServiceSettingsToWatermarkOptions, isWatermarkEnabled } from '../utils/watermark'
import { WATERMARK_TYPE } from './serviceSettings'
import { ProcessServerResponse, ItemRemovalServerResponse, ThunkAPIType } from '../types/watermark'

// TODO: テストが終わったら消す - モックテスト機能
// 使用方法: mockTestConfig.USE_MOCK_SERVER_RESPONSE を false にすると本番APIを使用
// テスト完了後は mockTestConfig オブジェクト全体と、それを使用している箇所を削除してください
const mockTestConfig = {
    // この値を true にすると、実際のサーバーレスポンスをモック化します
    USE_MOCK_SERVER_RESPONSE: false,

    // モック用の画像URL（実際の画像URLを使用）
    MOCK_IMAGE_URLS: [
        'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&h=600',  // ソファのリビング
        'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&h=600',  // モダンなリビング
        'https://images.unsplash.com/photo-1565182999561-18d7dc61c393?w=800&h=600', // 明るいリビング
    ],

    // サーバーレスポンスをモック化する関数
    createMockServerResponse(beforeImageId: number, stubId: number): ProcessServerResponse {
        // 初回生成は3枚固定
        const imageCount = 3
        const afterimages = []

        for (let i = 0; i < imageCount; i++) {
            afterimages.push({
                id: Math.floor(Math.random() * 100000) + 1000,
                downloadurl: this.MOCK_IMAGE_URLS[Math.floor(Math.random() * this.MOCK_IMAGE_URLS.length)],
                file_name: `mock_image_${Date.now()}_${i}.jpg`
            })
        }

        return {
            id: beforeImageId,
            file: this.MOCK_IMAGE_URLS[0], // 元画像もモック
            afterimages: afterimages,
            success: true
        }
    },

    // 追加画像のモックレスポンスを作成
    createAdditionalMockImage(beforeImageId: number): { id: number; downloadurl: string; file_name: string } {
        return {
            id: Math.floor(Math.random() * 100000) + 1000,
            downloadurl: this.MOCK_IMAGE_URLS[Math.floor(Math.random() * this.MOCK_IMAGE_URLS.length)],
            file_name: `mock_image_${Date.now()}.jpg`
        }
    }
}


interface BeforeImage {
    id?: number,
    stubId: number,
    roomType: string,
    lifestyle: string,
    hasTatami: boolean,
    file: File,
    uri: string,
    serverURI: string,
    isGeneratedFail: false,
    maxRegenCount: number,
}

interface AfterImage {
    id: number,
    before_image_id: number,
    serverURI: string,
    fileName: string,
    // ウォーターマーク関連
    watermarkEnabled?: boolean,
    watermarkedURI?: string,
    isProcessingWatermark?: boolean,
}

const addBeforeImages = createAsyncThunk(
    'addBeforeImages',
    async (files: FileList, thunkAPI: ThunkAPIType) => {
        const {beforeImages} = thunkAPI.getState().roomDesign
        let newImages = [...beforeImages]
        for (const file of files) {
            if (file.type != 'image/png' && file.type != 'image/jpeg' && file.type != 'image/webp') continue
            if (file.size >= MaxUploadFileSize) {
                thunkAPI.dispatch(roomDesignActions.setIsPhotoTooBigPopupShow(true))
                continue
            }
            if (newImages.length >= MaxPhotoUpload) break
            const uri = await readURIFromFile(file)
            const croppedFile = await getCroppedImage(file, uri)  // roomType引数を削除
            const croppedURI = await readURIFromFile(croppedFile)
            newImages.push({
                stubId: getRandId(),
                file: croppedFile,
                uri: croppedURI,
                hasTatami: false,
                maxRegenCount: MaxRegenCount,
            })
        }
        thunkAPI.dispatch(roomDesignActions.setBeforeImages(newImages))

        // 画像が追加された場合、最後に追加された画像を選択状態にする
        if (newImages.length > beforeImages.length) {
            const lastAddedImage = newImages[newImages.length - 1]
            thunkAPI.dispatch(roomDesignActions.setSelectedBeforeImage(lastAddedImage.stubId))
        }
    }
)

const setRoomType = createAsyncThunk(
    'setRoomType',
    async (roomType: string, thunkAPI: ThunkAPIType) => {
        const {beforeImages} = thunkAPI.getState().roomDesign
        const selectedBeforeImageData = selectBeforeImageData(thunkAPI.getState())
        const newImages = beforeImages.map((image: BeforeImage) => {
            if (image.stubId != selectedBeforeImageData.stubId) return image
            // oneRoom選択時はhasTatamiをfalseに設定
            return {
                ...image,
                roomType,
                hasTatami: roomType === 'oneRoom' ? false : image.hasTatami,
            }
        })
        thunkAPI.dispatch(roomDesignActions.setBeforeImages(newImages))
    }
)

const setLifestyle = createAsyncThunk(
    'setLifestyle',
    async (lifestyle: string, thunkAPI: ThunkAPIType) => {
        const {beforeImages} = thunkAPI.getState().roomDesign
        const selectedBeforeImageData = selectBeforeImageData(thunkAPI.getState())
        const newImages = beforeImages.map((image: BeforeImage) => {
            if (image.stubId != selectedBeforeImageData.stubId) return image
            return {
                ...image,
                lifestyle,
            }
        })
        thunkAPI.dispatch(roomDesignActions.setBeforeImages(newImages))
    }
)

const setHasTatami = createAsyncThunk(
    'setHasTatami',
    async (hasTatami: boolean, thunkAPI: ThunkAPIType) => {
        const {beforeImages} = thunkAPI.getState().roomDesign
        const selectedBeforeImageData = selectBeforeImageData(thunkAPI.getState())
        const newImages = beforeImages.map((image: BeforeImage) => {
            if (image.stubId != selectedBeforeImageData.stubId) return image
            return {
                ...image,
                hasTatami,
            }
        })
        thunkAPI.dispatch(roomDesignActions.setBeforeImages(newImages))
    }
)

const createRoomDesign = createAsyncThunk(
    'createRoomDesign',
    async (navigate: any, thunkAPI: ThunkAPIType) => {
        const {
            stubId,
            beforeImages,
        } = thunkAPI.getState().roomDesign
        thunkAPI.dispatch(roomDesignActions.setIsLoading(true))
        let runTime = 0
        thunkAPI.dispatch(roomDesignActions.setRuntime(runTime))
        const timer = setInterval(() => {thunkAPI.dispatch(roomDesignActions.setRuntime(runTime++))}, 1000)
        try {
            await beforeImagesGetAfterImages(thunkAPI, beforeImages, stubId)
            thunkAPI.dispatch(roomDesignActions.setIsLoading(false))
        } catch (e) {}
        clearInterval(timer)
        thunkAPI.dispatch(currentUserActions.getCurrentUser(navigate))
    }
)

function beforeImagesGetAfterImages(thunkAPI: ThunkAPIType, beforeImages: BeforeImage[], stubId: number) {
    const promises = beforeImages.map(beforeImage =>
        beforeImageGetAfterImages(beforeImage, thunkAPI, stubId)
    )
    return Promise.all(promises)
}

function beforeImageGetAfterImages(beforeImage: BeforeImage, thunkAPI: ThunkAPIType, stubId: number) {
    return new Promise(async (resolve, reject) => {
        const {roomType, lifestyle, hasTatami, file} = beforeImage

        // 生成進捗を初期化（合計3枚）
        thunkAPI.dispatch(roomDesignActions.setGenerationProgress({
            stubId: beforeImage.stubId,
            current: 0,
            total: 3
        }))

        // TODO: テストが終わったら消す - サーバーレスポンスモックの開始
        let res
        if (mockTestConfig.USE_MOCK_SERVER_RESPONSE) {
            // モックレスポンスを使用
            await new Promise(resolve => setTimeout(resolve, 3000)) // API呼び出しをシミュレート（3秒）
            const mockId = Math.floor(Math.random() * 10000) + 1
            const mockResponse = mockTestConfig.createMockServerResponse(mockId, beforeImage.stubId)
            res = processServerAfterImages(beforeImage.stubId, mockResponse, thunkAPI)
        } else {
            // 実際のAPIを使用
            // oneRoom選択時はlifestyleを空文字列、has_tatamiをfalseにする
            const apiLifestyle = roomType === 'oneRoom' ? '' : lifestyle
            const apiHasTatami = roomType === 'oneRoom' ? false : hasTatami

            // oneRoom選択時のみAPI送信直前にクロップ
            let finalFile = file;
            if (roomType === 'oneRoom') {
                const uri = await readURIFromFile(file)
                finalFile = await getCroppedImage(file, uri, roomType)
            }

            res = await RoomDesignService.firstgenAfterImagen({formData:{
                stub_id: stubId, room_type: roomType, has_tatami: apiHasTatami, lifestyle: apiLifestyle, before_image: finalFile}})
            .then(res => {
                return processServerAfterImages(beforeImage.stubId, res, thunkAPI)
            })
            .catch(res => {
                return processServerAfterImages(beforeImage.stubId, res, thunkAPI)
            })
        }
        // TODO: テストが終わったら消す - サーバーレスポンスモックの終了
        if (!res || !res.id) {
            // エラー時は進捗をクリア
            thunkAPI.dispatch(roomDesignActions.clearGenerationProgress(beforeImage.stubId))
            resolve(false)
            return
        }
        // モックモードでは初回生成で3枚返すため、追加取得は不要
        if (!mockTestConfig.USE_MOCK_SERVER_RESPONSE) {
            for (let i = 1; i <=2; i++) {
                const result = await getMoreAfterImage(beforeImage.stubId, res, thunkAPI)
                if (!result || !result.id) {
                    console.warn(`[beforeImageGetAfterImages] Additional image ${i}/2 failed`)
                }
            }
        }
        // 完了時に進捗を3/3に更新（クリアしない）
        thunkAPI.dispatch(roomDesignActions.setGenerationProgress({
            stubId: beforeImage.stubId,
            current: 3,
            total: 3
        }))
        resolve(true)
    })
}

function processServerAfterImages(stubId: number, res: ProcessServerResponse, thunkAPI: ThunkAPIType) {
    const {beforeImages, afterImages, watermarkGlobalEnabled} = thunkAPI.getState().roomDesign
    const newBeforeImages = beforeImages.map((beforeImage: BeforeImage) => {
        if (beforeImage.stubId != stubId) return beforeImage
        if (!res.id) {
            if (res.message != "cannot_generate_photo")
                thunkAPI.dispatch(toastActions.toastError(res.message))
            return {
                ...beforeImage,
                isGeneratedFail: true,
            }
        }

        return {
            ...beforeImage,
            isGeneratedFail: false,
            id: res.id,
            serverURI: res.file,
        }
    })
    thunkAPI.dispatch(roomDesignActions.setBeforeImages(newBeforeImages))
    if (!res.id) return res

    // 初回生成時は既存画像を削除、追加生成時は保持
    let newAfterImages = [...afterImages]

    // 初回生成の場合（afterimagesに複数画像がある場合）は既存画像を削除
    // 追加生成の場合（afterimagesに1枚のみ）は既存画像を保持
    if (res.afterimages && res.afterimages.length > 1) {
        // 初回生成時：同じbefore_image_idの画像を削除してから新規追加
        newAfterImages = afterImages.filter((afterImage: AfterImage) => afterImage.before_image_id != res.id)
    }

    res.afterimages.forEach((afterimage: { id: number; downloadurl: string; file_name: string }) => {
        newAfterImages.push({
            id: afterimage.id,
            before_image_id: res.id,
            serverURI: afterimage.downloadurl,
            fileName: afterimage.file_name,
        })
    })
    thunkAPI.dispatch(roomDesignActions.setAfterImages(newAfterImages))

    // 生成進捗を更新（API呼び出し1回 = 1枚としてカウント）
    const currentState = thunkAPI.getState().roomDesign
    const currentProgress = currentState.generatingImages[stubId]
    if (currentProgress) {
        thunkAPI.dispatch(roomDesignActions.setGenerationProgress({
            stubId: stubId,
            current: currentProgress.current + 1,
            total: currentProgress.total
        }))
    }

    // ウォーターマークが有効な場合、新しく追加された画像に自動的にウォーターマークを適用
    if (watermarkGlobalEnabled) {
        const serviceSettings = selectServiceSettings(thunkAPI.getState())

        if (serviceSettings && isWatermarkEnabled(serviceSettings)) {
            // 新しく追加された画像にウォーターマークを適用
            res.afterimages.forEach((afterimage: { id: number; downloadurl: string; file_name: string }) => {
                thunkAPI.dispatch(applyWatermarkToImage({
                    imageId: afterimage.id,
                    imageUrl: afterimage.downloadurl
                }))
            })
        }
    }

    return res
}

function getMoreAfterImage(stubId: number, res: { id: number; downloadurl: string; file_name: string }, thunkAPI: ThunkAPIType) {
    if (!res.id) return null

    console.log('[getMoreAfterImage] Called with:', { stubId, resId: res.id, mockMode: mockTestConfig.USE_MOCK_SERVER_RESPONSE })

    // TODO: テストが終わったら消す - サーバーレスポンスモックの開始
    if (mockTestConfig.USE_MOCK_SERVER_RESPONSE) {
        // モックレスポンスを使用
        console.log('[getMoreAfterImage] Using MOCK mode - generating mock response')
        return new Promise(resolve => setTimeout(resolve, 3000)) // API呼び出しをシミュレート（3秒）
            .then(() => {
                const mockResponse = mockTestConfig.createMockServerResponse(res.id, stubId)
                // 追加の画像を1枚だけ生成
                mockResponse.afterimages = [mockTestConfig.createAdditionalMockImage(res.id)]
                return processServerAfterImages(stubId, mockResponse, thunkAPI)
            })
    }
    // TODO: テストが終わったら消す - サーバーレスポンスモックの終了

    // 現在のUIで選択されている値を取得
    const { beforeImages } = thunkAPI.getState().roomDesign
    const currentBeforeImage = beforeImages.find((img: BeforeImage) => img.stubId === stubId)

    if (!currentBeforeImage) {
        // フォールバック: 見つからない場合はidのみ送信
        return RoomDesignService.regenAfterImages({id: res.id})
            .then(res => processServerAfterImages(stubId, res, thunkAPI))
            .catch(res => processServerAfterImages(stubId, res, thunkAPI))
    }

    // 現在のroomType, lifestyle, hasTatamiをAPIに送信
    const { roomType, lifestyle, hasTatami } = currentBeforeImage

    const requestBody = {
        room_type: roomType,
        lifestyle: roomType === 'oneRoom' ? undefined : lifestyle,
        has_tatami: roomType === 'oneRoom' ? false : hasTatami
    }

    console.log('[RegenAfterImages] Sending request:', {
        id: res.id,
        requestBody,
        currentBeforeImage: { roomType, lifestyle, hasTatami }
    })

    return RoomDesignService.regenAfterImages({
        id: res.id,
        requestBody
    })
        .then(res => {
            console.log('[RegenAfterImages] Response received:', res)
            return processServerAfterImages(stubId, res, thunkAPI)
        })
        .catch(res => {
            console.error('[RegenAfterImages] Request failed:', res)
            return processServerAfterImages(stubId, res, thunkAPI)
        })
}

const regenAfterImages = createAsyncThunk(
    'regenAfterImages',
    async (navigate: any, thunkAPI: ThunkAPIType) => {
        const selectedBeforeImageData = selectBeforeImageData(thunkAPI.getState())
        thunkAPI.dispatch(roomDesignActions.setIsLoading(true))
        thunkAPI.dispatch(roomDesignActions.setSelectedRegenImage(selectedBeforeImageData.stubId))
        let runTime = 0
        thunkAPI.dispatch(roomDesignActions.setRuntime(runTime))
        const timer = setInterval(() => {thunkAPI.dispatch(roomDesignActions.setRuntime(runTime++))}, 1000)
        try {
            const result = await beforeImageRegenAfterImages(selectedBeforeImageData, thunkAPI)
            // API成功時のみカウントを減らす
            if (result === true) {
                thunkAPI.dispatch(roomDesignActions.subtractMaxRegen(selectedBeforeImageData.stubId))
                thunkAPI.dispatch(toastActions.toastSuccess("処理済み画像を再生成しました"))
            } else {
                thunkAPI.dispatch(toastActions.toastError("再生成に失敗しました"))
            }
            thunkAPI.dispatch(roomDesignActions.setIsLoading(false))
            thunkAPI.dispatch(roomDesignActions.setSelectedRegenImage(undefined))
        } catch (e) {
            thunkAPI.dispatch(toastActions.toastError("再生成処理中にエラーが発生しました"))
            thunkAPI.dispatch(roomDesignActions.setIsLoading(false))
            thunkAPI.dispatch(roomDesignActions.setSelectedRegenImage(undefined))
        }
        clearInterval(timer)
        thunkAPI.dispatch(currentUserActions.getCurrentUser(navigate))
    }
)

function beforeImageRegenAfterImages(beforeImage: BeforeImage, thunkAPI: ThunkAPIType) {
    return new Promise(async (resolve, reject) => {
        if (!beforeImage.id) {
            resolve(false)
            return
        }

        // 再生成進捗を初期化（合計3枚）
        thunkAPI.dispatch(roomDesignActions.setGenerationProgress({
            stubId: beforeImage.stubId,
            current: 0,
            total: 3
        }))

        // 削除処理を除去し、既存画像を保持したまま新規追加のみ行う
        // clearAfterImages APIの呼び出しを削除
        // TODO: テストが終わったら消す - サーバーレスポンスモックの開始
        let res
        if (mockTestConfig.USE_MOCK_SERVER_RESPONSE) {
            // モックレスポンスを使用
            console.log('[beforeImageRegenAfterImages] Using MOCK mode - skipping actual API calls')
            res = {
                id: beforeImage.id,
                file: beforeImage.serverURI || beforeImage.uri,
                afterimages: [] // 追加処理なので空配列でOK
            }
        } else {
            // 実際のAPIを使用 - clearAfterImagesを呼ばずに直接resオブジェクトを作成
            res = {
                id: beforeImage.id,
                file: beforeImage.serverURI || beforeImage.uri,
                afterimages: [] // 既存画像は保持、新規追加のみ
            }
        }
        // TODO: テストが終わったら消す - サーバーレスポンスモックの終了

        if (!res || !res.id) {
            // エラー時は進捗をクリア
            thunkAPI.dispatch(roomDesignActions.clearGenerationProgress(beforeImage.stubId))
            resolve(false)
            return
        }

        // 3枚の新しい画像を追加生成（既存画像はそのまま保持）
        let successCount = 0
        for (let i = 1; i <=3; i++) {
            const result = await getMoreAfterImage(beforeImage.stubId, res, thunkAPI)
            // resultがあり、result.idが存在すれば成功
            if (result && result.id) {
                successCount++
            }
        }

        // 完了時に進捗をクリア
        thunkAPI.dispatch(roomDesignActions.clearGenerationProgress(beforeImage.stubId))

        // 少なくとも1枚でも成功していれば成功とみなす
        resolve(successCount > 0)
    })
}

// ウォーターマーク適用の非同期処理
const applyWatermarkToImage = createAsyncThunk(
    'roomDesign/applyWatermarkToImage',
    async ({ imageId, imageUrl }: { imageId: number; imageUrl: string }, thunkAPI: ThunkAPIType) => {
        const dispatch = thunkAPI.dispatch
        const state = thunkAPI.getState()

        dispatch(roomDesignActions.setImageWatermarkProcessing({ imageId, isProcessing: true }))

        try {
            // サービス設定からウォーターマーク設定を取得
            const watermarkSettings = selectServiceSettings(state)

            // ウォーターマークが有効かチェック
            if (!isWatermarkEnabled(watermarkSettings)) {
                console.error('Watermark is not enabled in service settings')
                throw new Error('Watermark is not enabled in service settings')
            }

            // 画像をBlobに変換
            const blob = await fetchImageAsBlob(imageUrl)

            // ウォーターマーク適用（現在の実装に合わせた設定）
            const watermarkOptions = convertServiceSettingsToWatermarkOptions(watermarkSettings)

            const result = await applyWatermark(
                blob,
                watermarkOptions,
                blob.size / (1024 * 1024)
            )

            // Blob URLではなくData URLを作成（CSP対策）
            const watermarkedURI = await blobToDataURL(result.blob)

            dispatch(roomDesignActions.setImageWatermarkStatus({
                imageId,
                enabled: true,
                watermarkedURI
            }))

            return { imageId, watermarkedURI }
        } catch (error) {
            console.error('Failed to apply watermark:', error)
            dispatch(toastActions.toastError('ウォーターマークの適用に失敗しました'))
            throw error
        } finally {
            dispatch(roomDesignActions.setImageWatermarkProcessing({ imageId, isProcessing: false }))
        }
    }
)

// 全画像へのウォーターマーク一括適用/解除
const toggleAllWatermarks = createAsyncThunk(
    'roomDesign/toggleAllWatermarks',
    async (enabled: boolean, thunkAPI: ThunkAPIType) => {
        const state = thunkAPI.getState()
        const dispatch = thunkAPI.dispatch
        const { afterImages } = state.roomDesign

        dispatch(roomDesignActions.setWatermarkGlobalEnabled(enabled))

        if (enabled) {
            // 全画像にウォーターマーク適用
            const promises = afterImages.map((image: AfterImage) =>
                dispatch(applyWatermarkToImage({
                    imageId: image.id,
                    imageUrl: image.serverURI
                }))
            )

            await Promise.all(promises)
        } else {
            // 全画像のウォーターマークを除去
            // Data URLの場合はrevokeObjectURLは不要
            dispatch(roomDesignActions.clearAllWatermarks())
        }
    }
)

const initialState = {
    isLoading: false,
    stubId: getRandId(),
    selectedBeforeImage: undefined,
    selectedRegenImage: undefined,
    selectedAfterImage: undefined,
    beforeImages: [] as BeforeImage[],
    afterImages: [] as AfterImage[],
    isConfirmPopupShow: false,
    isRoomTypePopupShow: false,
    isLifestylePopupShow: false,
    isClearAllPopupShow: false,
    isGeneratedFail: false,
    isRegenPopupShow: false,
    isPhotoTooBigPopupShow: false,
    maxExecTimePerImage: 75, //seconds
    runTime: 0,
    isEditPhotoPopupShow: false,
    isSaveChangesPhotoPopupShow: false,
    isDiscardChangesPhotoPopupShow: false,
    beforeImageRef: undefined as any,
    afterImageRef: undefined as any,
    // ウォーターマーク関連
    watermarkGlobalEnabled: false, // 初期値はfalse、コンポーネント側で設定に応じて変更
    processingWatermarks: [] as string[], // 処理中の画像ID
    // 生成進捗管理
    generatingImages: {} as Record<number, { current: number; total: number }>,
}

const roomDesignSlice = createSlice({
    name: 'roomDesign',
    initialState,
    reducers: {
        setIsLoading: (state, action) => {
            state.isLoading = action.payload
        },
        setBeforeImages: (state, action) => {
            state.beforeImages = action.payload
        },
        setSelectedBeforeImage: (state, action) => {
            state.selectedBeforeImage = action.payload
        },
        setSelectedRegenImage: (state, action) => {
            state.selectedRegenImage = action.payload
        },
        removeBeforeImage: (state, action) => {
            const removeStubId = action.payload
            state.beforeImages = state.beforeImages.filter(beforeImage => beforeImage.stubId != removeStubId)
        },
        setAfterImages: (state, action) => {
            state.afterImages = action.payload
        },
        setSelectedAfterImage: (state, action) => {
            state.selectedAfterImage = action.payload
        },
        setIsConfirmPopupShow: (state, action) => {
            state.isConfirmPopupShow = action.payload
        },
        setIsRoomTypePopupShow: (state, action) => {
            state.isRoomTypePopupShow = action.payload
        },
        setIsLifestylePopupShow: (state, action) => {
            state.isLifestylePopupShow = action.payload
        },
        setIsClearAllPopupShow: (state, action) => {
            state.isClearAllPopupShow = action.payload
        },
        setIsGeneratedFail: (state, action) => {
            state.isGeneratedFail = action.payload
        },
        setIsRegenPopupShow: (state, action) => {
            state.isRegenPopupShow = action.payload
        },
        setIsPhotoTooBigPopupShow: (state, action) => {
            state.isPhotoTooBigPopupShow = action.payload
        },
        setRuntime: (state, action) => {
            state.runTime = action.payload
        },
        subtractMaxRegen: (state, action) => {
            const stubId = action.payload
            state.beforeImages.forEach(image => {
                if (image.stubId != stubId) return
                image.maxRegenCount = image.maxRegenCount-1
            })
        },
        setIsEditPhotoPopupShow: (state, action) => {
            state.isEditPhotoPopupShow = action.payload
        },
        setIsSaveChangesPhotoPopupShow: (state, action) => {
            state.isSaveChangesPhotoPopupShow = action.payload
        },
        setIsDiscardChangesPhotoPopupShow: (state, action) => {
            state.isDiscardChangesPhotoPopupShow = action.payload
        },
        setBeforeImageRef: (state, action) => {
            state.beforeImageRef = action.payload
        },
        setAfterImageRef: (state, action) => {
            state.afterImageRef = action.payload
        },
        setInitState: (state) => {
            return initialState
        },
        // ウォーターマーク関連
        setWatermarkGlobalEnabled: (state, action: PayloadAction<boolean>) => {
            state.watermarkGlobalEnabled = action.payload
        },
        setImageWatermarkStatus: (state, action: PayloadAction<{
            imageId: number,
            enabled: boolean,
            watermarkedURI?: string,
        }>) => {
            const image = state.afterImages.find(img => img.id === action.payload.imageId)
            if (image) {
                image.watermarkEnabled = action.payload.enabled
                if (action.payload.watermarkedURI !== undefined) {
                    image.watermarkedURI = action.payload.watermarkedURI
                }
            }
        },
        setImageWatermarkProcessing: (state, action: PayloadAction<{
            imageId: number,
            isProcessing: boolean,
        }>) => {
            const image = state.afterImages.find(img => img.id === action.payload.imageId)
            if (image) {
                image.isProcessingWatermark = action.payload.isProcessing
            }
            // 処理中リストも更新
            if (action.payload.isProcessing) {
                if (!state.processingWatermarks.includes(String(action.payload.imageId))) {
                    state.processingWatermarks.push(String(action.payload.imageId))
                }
            } else {
                state.processingWatermarks = state.processingWatermarks.filter(id => id !== String(action.payload.imageId))
            }
        },
        clearAllWatermarks: (state) => {
            state.afterImages.forEach(image => {
                if (image.watermarkedURI) {
                    // Data URLを使用しているため、特別なクリーンアップは不要
                    image.watermarkedURI = undefined
                }
                image.watermarkEnabled = false
                image.isProcessingWatermark = false
            })
            state.processingWatermarks = []
        },
        // 生成進捗管理
        setGenerationProgress: (state, action: PayloadAction<{
            stubId: number,
            current: number,
            total: number,
        }>) => {
            state.generatingImages[action.payload.stubId] = {
                current: action.payload.current,
                total: action.payload.total,
            }
        },
        clearGenerationProgress: (state, action: PayloadAction<number>) => {
            delete state.generatingImages[action.payload]
        },
    },
})

const roomDesignActions = {
    ...roomDesignSlice.actions,
    addBeforeImages,
    setRoomType,
    setLifestyle,
    setHasTatami,
    createRoomDesign,
    regenAfterImages,
    applyWatermarkToImage,
    toggleAllWatermarks,
}
export {roomDesignActions}
export default roomDesignSlice.reducer